import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Any, Optional, Silent } from "@/__main__/data-model.mjs";
import { QUEUE_SYMBOLS } from "@/game-lol/constants/constants.mjs";
import QueueSymbol, {
  getQueueDetails,
} from "@/game-lol/utils/symbol-queue.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";

// Note this is not everything that is returned as part of the match, just what is being used currently
export const model = {
  // Additional meta-info.
  junglePathData: Silent(Any),
  benchmarkData: Silent(Any),
  localSummonerRole: Silent(RoleSymbol),

  endOfGameResult: Silent(String),
  gameId: Number,

  // TODO: type this as a Date pls!!!
  gameCreation: Number,

  gameDuration: Number,
  gameStartTimestamp: Number,
  gameEndTimestamp: Number,
  gameMode: Silent(String),
  gameName: Silent(String),
  gameType: Silent(String),
  gameVersion: String,
  mapId: Number,
  tournamentCode: Silent(String),
  participants: [
    {
      puuid: Silent(String),
      participantId: Number,

      // ==========================================
      // Hidden from external API, added by us.
      unencryptedSummonerId: Silent(Number),
      encryptedSummonerId: Silent(String),
      accountId: Silent(String),
      currentAccountId: Silent(String),
      derivedId: Silent(String),
      summonerLevel: Number,
      isLocalPlayer: Silent(Boolean),
      // ==========================================

      summonerId: Silent(String),
      summonerName: Silent(String), // Matches after 13.23 may not have this
      riotIdGameName: Silent(String), // Matches before 13.23 won't have this
      riotIdTagline: Silent(String), // Matches before 13.23 won't have this
      profileIcon: Number,
      championId: Number,
      championName: Silent(String),
      role: RoleSymbol,
      lane: RoleSymbol,
      summoner1Id: Number,
      summoner2Id: Number,
      teamId: Number,
      teamPosition: Silent(RoleSymbol),
      individualPosition: Silent(RoleSymbol),
      win: Boolean,
      gameEndedInSurrender: Boolean,
      challenges: Silent({
        skillshotsHit: Number,
        abilityUses: Number,
        quickSoloKills: Number,
        soloKills: Number,
        immobilizeAndKillWithAlly: Number,
        enemyChampionImmobilizations: Number,
        damageTakenOnTeamPercentage: Number,
        teamDamagePercentage: Number,
        effectiveHealAndShielding: Number,
        controlWardTimeCoverageInRiverOrEnemyHalf: Number,
      }),
      timePlayed: Number,
      perks: {
        statPerks: {
          defense: Number,
          flex: Number,
          offense: Number,
        },
        styles: [
          {
            style: Number,
            description: String,
            selections: [
              {
                perk: Number,
                var1: Number,
                var2: Number,
                var3: Number,
              },
            ],
          },
        ],
      },
      playerSubteamId: Number,
      subteamPlacement: Number,
      placement: Number,
      playerAugment1: Number,
      playerAugment2: Number,
      playerAugment3: Number,
      playerAugment4: Number,
      playerAugment5: Optional(Number),
      playerAugment6: Optional(Number),
      itemsPurchased: Optional(Number),
      item0: Number,
      item1: Number,
      item2: Number,
      item3: Number,
      item4: Number,
      item5: Number,
      item6: Number,
      // Stats
      stats: Silent(Any),
      kills: Number,
      deaths: Number,
      assists: Number,
      visionScore: Optional(Number),
      wardsPlaced: Optional(Number),
      wardsKilled: Optional(Number),
      sightWardsBoughtInGame: Number,
      detectorWardsPlaced: Optional(Number),
      killingSprees: Number,
      visionWardsBoughtInGame: Number,
      totalHeal: Number,
      totalUnitsHealed: Number,
      baronKills: Optional(Number),
      nexusKills: Optional(Number),
      largestKillingSpree: Number,
      largestMultiKill: Number,
      timeCcingOthers: Silent(Number),
      timeCCingOthers: Silent(Number),
      totalTimeCcDealt: Silent(Number),
      totalTimeCCDealt: Silent(Number),
      firstBloodKill: Boolean,
      totalDamageDealtToChampions: Number,
      physicalDamageDealtToChampions: Number,
      magicDamageDealtToChampions: Number,
      trueDamageDealtToChampions: Number,
      totalHealsOnTeammates: Number,
      totalDamageShieldedOnTeammates: Number,
      totalDamageDealt: Number,
      physicalDamageDealt: Number,
      magicDamageDealt: Number,
      trueDamageDealt: Number,
      largestCriticalStrike: Number,
      damageDealtToObjectives: Number,
      turretTakedowns: Optional(Number),
      damageDealtToTurrets: Number,
      damageDealtToBuildings: Number,
      totalDamageTaken: Number,
      physicalDamageTaken: Number,
      magicDamageTaken: Number,
      trueDamageTaken: Number,
      damageSelfMitigated: Number,
      goldEarned: Number,
      goldSpent: Number,
      totalMinionsKilled: Number,
      neutralMinionsKilled: Number,
      totalEnemyJungleMinionsKilled: Optional(Number),
      totalAllyJungleMinionsKilled: Optional(Number),
      nexusLost: Number,
      dragonKills: Optional(Number),
      turretKills: Number,
      inhibitorKills: Number,
      spell1Casts: Number,
      spell2Casts: Number,
      spell3Casts: Optional(Number),
      spell4Casts: Optional(Number),
      summoner1Casts: Optional(Number),
      summoner2Casts: Optional(Number),
      doubleKills: Number,
      tripleKills: Number,
      quadraKills: Number,
      pentaKills: Number,
      unrealKills: Number,
      onMyWayPings: Optional(Number),
      getBackPings: Optional(Number),
      enemyVisionPings: Optional(Number),
      assistMePings: Optional(Number),
      basicPings: Optional(Number),
      commandPings: Optional(Number),
      pushPings: Optional(Number),
      baitPings: Optional(Number),
      holdPings: Optional(Number),
      allInPings: Optional(Number),
      enemyMissingPings: Optional(Number),
      dangerPings: Optional(Number),
      needVisionPings: Optional(Number),
      visionClearedPings: Optional(Number),
      retreatPings: Optional(Number),
      eligibleForProgression: Optional(Boolean),
      objectivesStolenAssists: Optional(Number),
      objectivesStolen: Optional(Number),
      missions: Silent(Any),
      champExperience: Optional(Number),
      champLevel: Number,
      turretsLost: Optional(Number),
      inhibitorTakedowns: Optional(Number),
      championTransform: Optional(Number),
      bountyLevel: Optional(Number),
      teamEarlySurrendered: Boolean,
      gameEndedInEarlySurrender: Boolean,
      nexusTakedowns: Optional(Number),
      totalTimeSpentDead: Number,
      consumablesPurchased: Optional(Number),
      longestTimeSpentLiving: Number,
      inhibitorsLost: Optional(Number),
      firstBloodAssist: Boolean,
      firstInhibitorAssist: Optional(Boolean),
      firstInhibitorKill: Optional(Boolean),
      firstTowerAssist: Boolean,
      firstTowerKill: Boolean,

      // wtf is this?
      playerScore0: Optional(Number),
      playerScore1: Optional(Number),
      playerScore2: Optional(Number),
      playerScore3: Optional(Number),
      playerScore4: Optional(Number),
      playerScore5: Optional(Number),
      playerScore6: Optional(Number),
      playerScore7: Optional(Number),
      playerScore8: Optional(Number),
      playerScore9: Optional(Number),
      playerScore10: Optional(Number),
      playerScore11: Optional(Number),
      // RIOTTTTTTTTTT
      PlayerScore0: Optional(Number),
      PlayerScore1: Optional(Number),
      PlayerScore2: Optional(Number),
      PlayerScore3: Optional(Number),
      PlayerScore4: Optional(Number),
      PlayerScore5: Optional(Number),
      PlayerScore6: Optional(Number),
      PlayerScore7: Optional(Number),
      PlayerScore8: Optional(Number),
      PlayerScore9: Optional(Number),
      PlayerScore10: Optional(Number),
      PlayerScore11: Optional(Number),
    },
  ],
  platformId: String,
  queueId: QueueSymbol,
  queue: Silent(String),
  seasonId: Silent(Number),
  teams: [
    {
      teamId: Number,
      win: Boolean,
      isWinningTeam: Silent(Boolean),
      bans: [
        {
          championId: Number,
          pickTurn: Number,
        },
      ],
      feats: Optional({
        epicMonsterKill: {
          featState: Number,
        },
        firstBlood: {
          featState: Number,
        },
        firstTurret: {
          featState: Number,
        },
      }),
      objectives: Optional({
        baron: {
          first: Boolean,
          kills: Number,
        },
        champion: {
          first: Boolean,
          kills: Number,
        },
        dragon: {
          first: Boolean,
          kills: Number,
        },
        horde: {
          first: Boolean,
          kills: Number,
        },
        inhibitor: {
          first: Boolean,
          kills: Number,
        },
        riftHerald: {
          first: Boolean,
          kills: Number,
        },
        atakhan: {
          first: Boolean,
          kills: Number,
        },
        tower: {
          first: Boolean,
          kills: Number,
        },
      }),
    },
  ],
} satisfies ModelDefinition;

const MatchModel = createModel({
  data: {
    match: { info: model },
  },
}).transform(model, (data) => {
  const match = data.data.match.info;
  if (match.gameType === getQueueDetails(QUEUE_SYMBOLS.custom).gql) {
    match.queueId = QUEUE_SYMBOLS.custom;
  }

  match.participants = match.participants.reduce((acc, p, index) => {
    acc[index] = {
      ...p,
      totalTimeCcDealt: p.totalTimeCCDealt || p.totalTimeCcDealt,
    };
    return acc;
  }, []);

  return match;
});

export type Match = FromModel<typeof MatchModel>;
export type MatchParticipant = Match["participants"][0];
export default MatchModel;
